import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useForkRef } from "@mui/material/utils";

type ScrollableVariant = "default" | "compact";

const DIMENSIONS: Record<
  ScrollableVariant,
  { trackW: number; thumbW: number; thumbMin: number; thumbLeft: number }
> = {
  default: { trackW: 8, thumbW: 4, thumbMin: 20, thumbLeft: 2 },
  compact: { trackW: 5, thumbW: 3, thumbMin: 12, thumbLeft: 0.5 },
};

export interface CustomScrollbarsProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  sx?: object;
  trackOffsetTop?: number;
  trackOffsetBottom?: number;
  showOverlayTrack?: boolean;
  variant?: ScrollableVariant;
  scrollableElement?: "div" | "ul";
  preserveScrollOnGrow?: boolean;
  /** "vertical" keeps the native horizontal bar, which wide tables need. */
  hideNative?: "both" | "vertical";
  /** Reserve the overlay track's width so right-edge controls stay clickable. */
  trackGutter?: boolean;
  scrollableProps?: Omit<
    React.HTMLAttributes<HTMLUListElement>,
    "children" | "ref"
  >;
}

const CustomScrollbars = forwardRef<
  HTMLDivElement | HTMLUListElement,
  CustomScrollbarsProps
>(function CustomScrollbars(
  {
    children,
    style,
    className,
    sx,
    trackOffsetTop = 0,
    trackOffsetBottom = 0,
    showOverlayTrack,
    variant = "default",
    scrollableElement = "div",
    scrollableProps,
    preserveScrollOnGrow = false,
    hideNative = "both",
    trackGutter = false,
  },
  ref,
) {
  const { trackW, thumbW, thumbMin, thumbLeft } = DIMENSIONS[variant];
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const overlayEnabled = showOverlayTrack ?? !isSmDown;
  const viewRef = useRef<HTMLDivElement | HTMLUListElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const [thumbTop, setThumbTop] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(20);
  const [visible, setVisible] = useState(false);
  const [dragging, setDragging] = useState(false);
  const dragStartY = useRef(0);
  const dragStartScrollTop = useRef(0);
  const lastScrollTopRef = useRef(0);
  const prevChildCountRef = useRef(0);

  const {
    onScroll: scrollableOnScroll,
    className: scrollableClassName,
    style: scrollableStyle,
    ...restScrollableUlist
  } = scrollableProps || {};
  const forkedViewRef = useForkRef(
    viewRef,
    ref as React.Ref<HTMLDivElement> & React.Ref<HTMLUListElement>,
  );

  const updateThumb = useCallback(() => {
    const el = viewRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const show = scrollHeight > clientHeight;
    setVisible(show);
    if (!show) return;
    const trackHeight = Math.max(
      0,
      el.clientHeight - trackOffsetTop - trackOffsetBottom,
    );
    const thumbH = Math.max(
      thumbMin,
      (clientHeight / scrollHeight) * trackHeight,
    );
    const maxScroll = scrollHeight - clientHeight;
    const thumbTopPx =
      maxScroll <= 0 ? 0 : (scrollTop / maxScroll) * (trackHeight - thumbH);
    setThumbHeight(thumbH);
    setThumbTop(thumbTopPx);
  }, [trackOffsetTop, trackOffsetBottom, thumbMin]);

  useEffect(() => {
    const el = viewRef.current;
    if (!el) return;
    updateThumb();
    const ro = new ResizeObserver(updateThumb);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateThumb, children]);

  useEffect(() => {
    const t = setTimeout(updateThumb, 0);
    return () => clearTimeout(t);
  }, [updateThumb, children]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement | HTMLUListElement>) => {
      lastScrollTopRef.current = e.currentTarget.scrollTop;
      scrollableOnScroll?.(e as React.UIEvent<HTMLUListElement>);
      if (dragging) return;
      updateThumb();
    },
    [scrollableOnScroll, dragging, updateThumb],
  );

  const childCount = React.Children.count(children);
  useEffect(() => {
    const grew = childCount > prevChildCountRef.current;
    const target = lastScrollTopRef.current;
    prevChildCountRef.current = childCount;
    if (!preserveScrollOnGrow || !grew || target <= 0) return;
    const raf = requestAnimationFrame(() => {
      const el = viewRef.current;
      if (!el) return;
      el.scrollTop = target;
      updateThumb();
    });
    return () => cancelAnimationFrame(raf);
  }, [childCount, preserveScrollOnGrow, updateThumb]);

  const handleThumbMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (!viewRef.current) return;
    setDragging(true);
    dragStartY.current = e.clientY;
    dragStartScrollTop.current = viewRef.current.scrollTop;
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const el = viewRef.current;
      if (!el) return;
      const dy = e.clientY - dragStartY.current;
      const trackHeight = Math.max(
        0,
        el.clientHeight - trackOffsetTop - trackOffsetBottom,
      );
      const { scrollHeight, clientHeight } = el;
      const maxScroll = scrollHeight - clientHeight;
      if (maxScroll <= 0) return;
      const thumbH = Math.max(
        thumbMin,
        (clientHeight / scrollHeight) * trackHeight,
      );
      const trackThumbSpace = trackHeight - thumbH;
      const scrollPerPx = maxScroll / trackThumbSpace;
      const newScroll = Math.max(
        0,
        Math.min(maxScroll, dragStartScrollTop.current + dy * scrollPerPx),
      );
      el.scrollTop = newScroll;
      updateThumb();
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging, trackOffsetTop, trackOffsetBottom, updateThumb, thumbMin]);

  const handleTrackClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const el = viewRef.current;
      const track = e.currentTarget;
      if (!el || !track || dragging) return;
      const rect = track.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const trackHeight = rect.height;
      const { scrollHeight, clientHeight } = el;
      const maxScroll = scrollHeight - clientHeight;
      if (maxScroll <= 0) return;
      const thumbH = Math.max(
        thumbMin,
        (clientHeight / scrollHeight) * trackHeight,
      );
      const trackThumbSpace = trackHeight - thumbH;
      const ratio = Math.max(
        0,
        Math.min(1, (y - thumbH / 2) / trackThumbSpace),
      );
      el.scrollTop = ratio * maxScroll;
    },
    [dragging, thumbMin],
  );

  const thumbColor =
    variant === "compact" ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.25)";
  const thumbHoverColor =
    variant === "compact" ? "rgba(0, 0, 0, 0.35)" : "rgba(0, 0, 0, 0.4)";

  // Below sm the overlay thumb is off, so hiding the native bar too would leave
  // no scroll affordance at all.
  const hideNativeClass = !overlayEnabled
    ? undefined
    : hideNative === "vertical"
      ? "custom-scrollbars-hide-native-y"
      : "custom-scrollbars-hide-native";
  const gutterPx = trackGutter && overlayEnabled ? trackW : 0;
  // A pane with nothing to scroll has no boundary to defend - keeping "contain"
  // there swallows the wheel instead of passing it to the parent scroller.
  const overscrollBehavior = visible ? ("contain" as const) : ("auto" as const);
  const ulListClass = [hideNativeClass, scrollableClassName]
    .filter(Boolean)
    .join(" ");

  const scrollableSxUlist = {
    flex: 1,
    overflow: "auto",
    minHeight: 0,
    overscrollBehavior,
    WebkitOverflowScrolling: "touch" as const,
    overflowX: "hidden" as const,
    ...(gutterPx ? { paddingRight: `${gutterPx}px` } : undefined),
  };

  return (
    <Box
      className={className}
      sx={{
        position: "relative",
        display: "flex",
        flex: 1,
        minHeight: 0,
        // A flex child that cannot shrink pushes its row wider than the
        // viewport, so the page grows sideways instead of scrolling inside.
        minWidth: 0,
        ...(scrollableElement === "ul" ? { width: "100%" } : undefined),
        ...sx,
      }}
      style={style}
    >
      {scrollableElement === "ul" ? (
        <Box
          component="ul"
          ref={forkedViewRef as React.Ref<HTMLUListElement>}
          onScroll={handleScroll}
          className={ulListClass}
          style={scrollableStyle}
          sx={scrollableSxUlist}
          {...restScrollableUlist}
        >
          {children}
        </Box>
      ) : (
        <Box
          ref={forkedViewRef as React.Ref<HTMLDivElement>}
          onScroll={handleScroll}
          className={hideNativeClass}
          sx={{
            flex: 1,
            overflow: "auto",
            minHeight: 0,
            overscrollBehavior,
            WebkitOverflowScrolling: "touch",
            ...(gutterPx ? { paddingRight: `${gutterPx}px` } : undefined),
          }}
        >
          {children}
        </Box>
      )}
      {visible && overlayEnabled && (
        <Box
          onClick={handleTrackClick}
          sx={{
            position: "absolute",
            top: trackOffsetTop,
            right: 0,
            width: trackW,
            height:
              trackOffsetTop > 0 || trackOffsetBottom > 0
                ? `calc(100% - ${trackOffsetTop + trackOffsetBottom}px)`
                : "100%",
            borderRadius: 1,
            backgroundColor: "transparent",
            cursor: "pointer",
            pointerEvents: "auto",
            zIndex: 20,
          }}
        >
          <Box
            ref={thumbRef}
            onMouseDown={handleThumbMouseDown}
            sx={{
              position: "absolute",
              left: thumbLeft,
              width: thumbW,
              top: thumbTop,
              height: thumbHeight,
              borderRadius: 1,
              backgroundColor: thumbColor,
              cursor: "grab",
              "&:hover": { backgroundColor: thumbHoverColor },
              "&:active": { cursor: "grabbing" },
            }}
          />
        </Box>
      )}
    </Box>
  );
});

export default CustomScrollbars;
